import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { FormGroup, Label, Input, Button, Row, Col, Card, CardHeader, CardBody, FormFeedback } from 'reactstrap';
import { OnChangeCallback } from '../../../../../util/UtilityTypes'
import { CoreStringValueBuilderPart, StringValueBuilderPartValueSource } from '../../../../../models/CoreModels';
import SearchableSelectInput from '../../../../misc/SearchableSelectInput';
import OrderableList from '../../../../misc/OrderableList';
import { useTranslation } from 'react-i18next';

interface StringValueBuilderInputProps {
    name: string
    parts: CoreStringValueBuilderPart[]
    disabled?: boolean
    onChange: OnChangeCallback<CoreStringValueBuilderPart[]>
}

interface StringValueBuilderPartFormProps {
    name: string
    disabled?: boolean
    onAdd: (part: CoreStringValueBuilderPart) => void
}


type StringValueBuilderPartField = keyof CoreStringValueBuilderPart;

type FieldValidation = { field: StringValueBuilderPartField, message: string };

type StringValueBuilderPartDefinitions = {
    [key: number]: {
        description: string,
        fields?: StringValueBuilderPartField[],
        requiredFields?: StringValueBuilderPartField[],
        validate: (part: CoreStringValueBuilderPart) => FieldValidation[]
        render: (part: CoreStringValueBuilderPart) => string,
        example: (part: CoreStringValueBuilderPart) => string
    }
}

const partDefinitions: StringValueBuilderPartDefinitions = {
    
    [StringValueBuilderPartValueSource.PERSON_FIRST_NAME]: {
        description: 'stringValueBuilder.partDefinitions.personFirstName.description',
        fields: ["maxLength"],
        validate: (part) => {
            const validation: FieldValidation[] = [];
            if (part.maxLength !== undefined && part.maxLength < 1) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.personFirstName.validationMessage' });
            }

            return validation;
        },
        render: (part) => {
            if (part.maxLength !== undefined && part.maxLength !== null) {
                return `stringValueBuilder.partDefinitions.personFirstName.firstNameRender`;
            } else {
                return `stringValueBuilder.partDefinitions.personFirstName.description`;
            }
        },
        example: (part) => {
            if (part.maxLength !== undefined && part.maxLength !== null) {
                return "kalle".substring(0, part.maxLength);
            } else {
                return "kalle";
            }
        }
    },
    [StringValueBuilderPartValueSource.PERSON_LAST_NAME]: {
        description: 'stringValueBuilder.partDefinitions.personLastName.description',
        fields: ["maxLength"],
        validate: (part) => {
            const validation: FieldValidation[] = [];

            if (part.maxLength !== undefined && part.maxLength < 1) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.personLastName.validationMessage' });
            }

            return validation;
        },
        render: (part) => {
            if (part.maxLength !== undefined && part.maxLength !== null) {
                return 'stringValueBuilder.partDefinitions.personLastName.lastNameRender';
            } else {
                return `stringValueBuilder.partDefinitions.personLastName.description`;
            }
        },
        example: (part) => {
            if (part.maxLength !== undefined && part.maxLength !== null) {
                return "kula".substring(0, part.maxLength);
            } else {
                return "kula";
            }
        }
    },
    [StringValueBuilderPartValueSource.RANDOM_FROM_CHARSET]: {
        description: 'stringValueBuilder.partDefinitions.randomFromCharset.description',
        fields: ["minLength", "maxLength", "valueText"],
        requiredFields: ["minLength", "maxLength", "valueText"],
        validate: (part) => {
            const validation: FieldValidation[] = [];

            if (part.valueText === undefined || part.valueText.trim() === "") {
                validation.push({ field: "valueText", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageValueTexthUndefined' });
            }

            if (part.minLength === undefined) {
                validation.push({ field: "minLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMinLengthUndefined'  });
            } else if (part.minLength < 1) {
                validation.push({ field: "minLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMinLength' });
            }

            if (part.maxLength === undefined) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMaxLengthUndefined' });
            }
            else if (part.maxLength > 2000) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMaxLength'  });
            }
            else if (part.maxLength < (part.minLength ?? 0)) {
                validation.push({ field: "minLength", message: 'stringValueBuilder.partDefinitions.randomFromCharset.validationMessageMaxLowerMin' });
            }

            return validation;
        },
        render: (part) => {
            if (part.minLength === part.maxLength) {
                return 'stringValueBuilder.partDefinitions.randomFromCharset.render1';
            } else {
                return `stringValueBuilder.partDefinitions.randomFromCharset.render2`;
            }
        },
        example: (part) => {
            if (!part.maxLength || !part.valueText || !part.minLength) {
                return "";
            }

            let length = part.minLength + Math.floor((Math.random() * (part.maxLength - part.minLength + 1)));

            var str = "";
            for (let i = 0; i < length; i++) {
                str += part.valueText[Math.floor(Math.random() * part.valueText.length)]
            }

            return str;
        }
    },
    [StringValueBuilderPartValueSource.STUDENT_SCHOOL_YEAR]: {
        description: 'stringValueBuilder.partDefinitions.studentSchoolYear.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.studentSchoolYear.description',
        example: (_) => '3'
    },
    [StringValueBuilderPartValueSource.PERIOD_AWARE_START_YEAR]: {
        description: 'stringValueBuilder.partDefinitions.periodAwareStartYear.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.periodAwareStartYear.schoolYearStart',
        example: (_) => {
            const date = new Date();
            if (date.getMonth() < 6) {
                return (date.getFullYear() - 1).toString()
            } else {
                return date.getFullYear().toString()
            }
        }
    },
    [StringValueBuilderPartValueSource.PERIOD_AWARE_END_YEAR]: {
        description: 'stringValueBuilder.partDefinitions.periodAwareEndYear.description',
        validate: _ => [],
        render: (_) => 'stringValueBuilder.partDefinitions.periodAwareEndYear.schoolYearEnd',
        example: (_) => {
            const date = new Date();
            if (date.getMonth() < 6) {
                return date.getFullYear().toString()
            } else {
                return (date.getFullYear() + 1).toString()
            }
        }
    },
    [StringValueBuilderPartValueSource.CONSTANT]: {
        description:'stringValueBuilder.partDefinitions.constant.description',
        fields: ["valueText"],
        requiredFields: ["valueText"],
        validate: (part) => {
            const validation: FieldValidation[] = [];

            // NOTE: Do not trim and check for whitespace since there is a possibility of wanting to use white space characters.
            if (part.valueText === undefined || part.valueText === "") {
                validation.push({ field: "valueText", message: 'stringValueBuilder.partDefinitions.constant.validationMessage' });
            }

            return validation;
        },
        render: (part) => `"${part.valueText}"`,
        example: (part) => part.valueText || ""
    }, 
    [StringValueBuilderPartValueSource.YEAR_OF_BIRTH]: {
        description: 'stringValueBuilder.partDefinitions.yearOfBirth.description',
        fields: ["maxLength"],
        validate: (part) => {
            const validation: FieldValidation[] = [];
          
            if ((part.maxLength !== undefined && part.maxLength !== null) && (part.maxLength < 1 || part.maxLength > 4)) {
                validation.push({ field: "maxLength", message: 'stringValueBuilder.partDefinitions.yearOfBirth.validationMessageMaxLength' });
            }

            return validation;
        },
        render: (part) => {
            if (part.maxLength === undefined || part.maxLength === null) {
                return 'stringValueBuilder.partDefinitions.yearOfBirth.renderDefaultMaxLength';
            }
            else {
                return 'stringValueBuilder.partDefinitions.yearOfBirth.render';
            }
        },
        example: (part) => {
            const date = new Date();
            var ex = date.getFullYear().toString();

            if (!part.maxLength)
                return ex + ' '; 

            let length = ex.length; 
            return ex.substring(length - part.maxLength) + ' ';
        },
    }
}

const StringValueBuilderPartForm: React.FC<StringValueBuilderPartFormProps> = ({ name, disabled, onAdd }) => {
    const [formPart, setFormPart] = useState<CoreStringValueBuilderPart>({ valueSource: StringValueBuilderPartValueSource.UNKNOWN });
    const [invalid, setInvalid] = useState<FieldValidation[]>([]);
    const { t } = useTranslation(['configuration', 'common'])

    useEffect(() => {
        setInvalid([]);
    }, [formPart.valueSource, formPart.valueText, formPart.minLength, formPart.maxLength])

    const saveNew = useCallback(() => {
        const validation = partDefinitions[formPart.valueSource]?.validate(formPart) ?? [{field: "valueSource", message: t('stringValueBuilder.partForm.mustBeSpecified', {ns: 'configuration'})}];

        if (validation.length !== 0) {
            setInvalid(validation)
            return;
        }

        onAdd(formPart);

        setFormPart({ valueSource: StringValueBuilderPartValueSource.UNKNOWN });
    }, [onAdd, formPart, setFormPart])


    const show = (field: string) => {
        return partDefinitions[formPart.valueSource]?.fields?.some(x => x === field) ?? false;
    }

    const isRequired = (field: string) => {
        return partDefinitions[formPart.valueSource]?.requiredFields?.some(x => x === field) ?? false;
    }

    return (
        <div>
            <Row>
                <Col xs="3">
                    <FormGroup>
                        <Label>{t('stringValueBuilder.partForm.valueSource.label', {ns: 'configuration'})}</Label>
                        <SearchableSelectInput
                            name={`${name}.valueSource`}
                            defaultText={t('stringValueBuilder.partForm.valueSource.defaultText', {ns: 'configuration'})}
                            selected={formPart.valueSource}
                            disabled={disabled}
                            onChange={(_, v) => {
                                if (v) {
                                    //reset all values on typeswitch
                                    setFormPart({ valueSource: v })
                                } else {
                                    setFormPart({ valueSource: StringValueBuilderPartValueSource.UNKNOWN })
                                }
                            }}
                            items={Object.entries(partDefinitions).map(([key, value]) => ({ value: parseInt(key, 10), label: t(value.description, {ns: 'configuration'}) }))}

                        />
                    </FormGroup>
                </Col>
                {show("valueText") &&
                    <Col xs="3">
                        <FormGroup>
                            <Label for={`${name}.valueText`}>{t('stringValueBuilder.partForm.valueText.label', {ns: 'configuration'})} {isRequired('valueText') ? '*' : ''}</Label>
                            <Input
                                type="text"
                                name={`${name}.valueText`}
                                id={`${name}.valueText`}
                                className="w-100"
                                value={formPart.valueText || ''}
                                disabled={disabled}
                                onChange={(e) => setFormPart({ ...formPart, valueText: e.target.value })}
                                invalid={invalid.some(x => x.field === "valueText")}
                            />
                            <FormFeedback>{t(invalid.filter(x => x.field === "valueText")[0]?.message, {ns: 'configuration'})}</FormFeedback>
                        </FormGroup>
                    </Col>
                }
                {show("minLength") &&
                    <Col xs="2">
                        <FormGroup>
                            <Label for={`${name}.minLength`}>{t('stringValueBuilder.partForm.minLength.label', {ns: 'configuration'})} {isRequired('minLength') ? '*' : ''}</Label>
                            <Input
                                type="number"
                                title={t('stringValueBuilder.partForm.minLength.title', {ns: 'configuration'})}
                                name={`${name}.minLength`}
                                className="w-100"
                                value={formPart.minLength === undefined ? '' : formPart.minLength}
                                disabled={disabled}
                                onChange={e => setFormPart({ ...formPart, minLength: isNaN(parseInt(e.target.value, 10)) ? undefined : parseInt(e.target.value, 10) })}
                                invalid={invalid.some(x => x.field === "minLength")}
                            />
                            <FormFeedback>{t(invalid.filter(x => x.field === "minLength")[0]?.message, {ns: 'configuration'})}</FormFeedback>
                        </FormGroup>
                    </Col>
                }
                {show("maxLength") &&
                    <Col xs="2">
                        <FormGroup>
                            <Label for={`${name}.maxLength`}>{t('stringValueBuilder.partForm.maxLength.label', {ns: 'configuration'})}{isRequired('maxLength') ? '*' : ''}</Label>
                            <Input
                                type="number"
                                title={t('stringValueBuilder.partForm.maxLength.title', {ns: 'configuration'})}
                                name={`${name}.maxLength`}
                                className="w-100"
                                value={formPart.maxLength === undefined ? '' : formPart.maxLength}
                                disabled={disabled}
                                onChange={e => setFormPart({ ...formPart, maxLength: isNaN(parseInt(e.target.value, 10)) ? undefined : parseInt(e.target.value, 10) })}
                                invalid={invalid.some(x => x.field === "maxLength")}
                            />
                            <FormFeedback>{t(invalid.filter(x => x.field === "maxLength")[0]?.message, {ns: 'configuration'})}</FormFeedback>
                        </FormGroup>
                    </Col>
                }
                <Col xs="2">
                    <Button style={{ marginTop: "2rem" }} onClick={saveNew} disabled={disabled || formPart.valueSource === StringValueBuilderPartValueSource.UNKNOWN || invalid.length !== 0}>{t('action.add', {ns: 'common'})}</Button>
                </Col>
            </Row>

        </div >
    );
}


const StringValueBuilderInput: React.FC<StringValueBuilderInputProps> = ({ parts, onChange, disabled, name}) => {
   const { t } = useTranslation(['configuration', 'common']) 
    const onAdd = useCallback((part: CoreStringValueBuilderPart) => {
        onChange(name, [...parts, part]);
    }, [parts, name, onChange]);

    const renderItem = useCallback((part: CoreStringValueBuilderPart) => {
        return t(partDefinitions[part.valueSource]?.render(part), {ns: 'configuration', MaxLength: part.maxLength, MinLength: part.minLength, ValueSource: part.valueSource, ValueText: part.valueText }) ?? t('stringValueBuilder.unknown', {ns: 'configuration'});
    }, []);

    const example = useMemo(() => {
        return parts.reduce((acc: string | undefined, part: CoreStringValueBuilderPart) => {
            var partStr = partDefinitions[part.valueSource]?.example(part) ?? "";
            return acc + partStr;
        }, "");
    }, [parts])

    return (
        <Card className="mb-3">
            <CardHeader className="d-block">
                {t('stringValueBuilder.header', {ns: 'configuration'})}
            </CardHeader>
            <CardBody>
                {parts.length > 0 && <>
                    <OrderableList name={name} disabled={disabled} parts={parts} onChange={onChange} renderItem={renderItem} />
                    <div className="py-3">{t('stringValueBuilder.example', {ns: 'configuration'})} <span>{example}</span></div>
                </>}

                <StringValueBuilderPartForm name={name} disabled={disabled} onAdd={onAdd} />
            </CardBody>
        </Card>
    );
}

export default StringValueBuilderInput;
